import React from 'react';
import { get } from 'lodash';
import moment from 'moment';
import Grid from 'semantic-ui-react/dist/commonjs/collections/Grid';
import { Field } from 'redux-form';
import { API_URL } from '../config';
import { flattenObject, encryptData } from '../library';
import RenderTextField from '../../components/form-controls/RenderTextField';
import RenderDatePicker from '../../components/form-controls/RenderDatePicker';
import { dynamic_validator2, requiredradio } from '../validation';
import { CommonDropdownOutlined, CommonDropdown } from '@Components';
import { r_maxLength } from '../validation/restrictions';
import { updateDocsUploadedOrEverifiedSuccess } from '../../redux/actions/common/common-action';
import {
  ENCRYPT_DATA_FOR_DOCUMENT_CODE,
  DISABLE_FIELDS_ON_EVERIFICATION_ATTEMPT,
  MANDATORY_DOCUMENT_UPLOAD_FOR,
  EXCLUDE_KEYS_IN_DOC_DETAILS,
  DONOT_INITIALIZE_DOCS,
  TEXT_TYPE_DOCUMENTS,
  ADDRESS_POA_DOCUMENT
} from '../../utils/constants';
import renderRadioButtonError from '../../components/form-controls/RenderRadioButtonError';
import { store } from '../../store';
import { isValidBeneAddressInput } from '../../screens/beneficiary-flow/pages/recipient-address/utils/methods';

/**
 * function to return render friendly object for document rendering
 * also handles initialization of radio button and file input
 * @param {object} uploadDocConfig
 * @param {array} documentDetails
 * returns Object
 * Object = {
 *  documentType:{
 *    documentSequence:[
 *      0: DocumentConfiguration
 *      ...
 *    ]
 *  }
 * }
 */
export const changeConfigOrder = ({ processesdConfig }, country_code) => {
  if (country_code === 'CA') {
    const addCheckDL = [...processesdConfig.INDIVIDUAL_POI[1]].map((obj) => {
      if (obj.document_code === 'DRIVING_LICENSE') {
        return {
          ...obj,
          radio_checked: false
        };
      } else {
        return {
          ...obj
        };
      }
    });
    const newPOI =
      processesdConfig.INDIVIDUAL_POI[1].findIndex(
        (obj) => obj.document_code === 'DRIVING_LICENSE'
      ) === 0
        ? addCheckDL.reverse()
        : addCheckDL;
    return {
      processesdConfig: { ...processesdConfig, INDIVIDUAL_POI: { 1: newPOI } }
    };
  } else {
    return { processesdConfig };
  }
};

const processNestedFieldsConfig = (ele) => {
  if (ele?.document_code === ADDRESS_POA_DOCUMENT) {
    const fieldsJson = ele.fields_json;

    if (Array.isArray(fieldsJson)) {
      fieldsJson.forEach((field) => {
        if (field.type === 'radio' && Array.isArray(field.values)) {
          let showCaptureButton = false;
          let showUploadButton = false;
          field.values.forEach((value) => {
            if (value.show_capture_button) {
              showCaptureButton = true;
            }
            if (value.show_upload_button) {
              showUploadButton = true;
            }
          });
          ele.show_capture_button = showCaptureButton;
          ele.show_upload_button = showUploadButton;
        }
      });
    }
  }
};
export function processesdScreenConfig(uploadDocConfig, documentDetails = [], everificationData) {
  let processesdConfig = {},
    existingDocTypes = [],
    dependentFields = [],
    disabledFields = [];
  if (documentDetails && documentDetails.length > 0) {
    //for radio
    existingDocTypes = documentDetails.map((key) => key.document_code);
  }
  if (everificationData) {
    disabledFields = everificationData.everification_request_field_data
      ? Object.keys(everificationData.everification_request_field_data).map(
          (k) => everificationData.everification_request_field_data[k]
        )
      : [];
  }
  uploadDocConfig.forEach((ele) => {
    processNestedFieldsConfig(ele);
    if (!processesdConfig[ele.type]) {
      processesdConfig[ele.type] = {};
    }
    if (!processesdConfig[ele.type][ele.sequence]) {
      processesdConfig[ele.type][ele.sequence] = [];
    }
    let length = processesdConfig[ele.type][ele.sequence].push(ele),
      elementFieldJson = processesdConfig[ele.type][ele.sequence][length - 1].fields_json;
    if (
      existingDocTypes.includes(processesdConfig[ele.type][ele.sequence][length - 1].document_code)
    ) {
      processesdConfig[ele.type][ele.sequence][length - 1].radio_checked = true; //radio button initialization
    }
    if (ele.rfi_request) {
      elementFieldJson = elementFieldJson.filter((k) => ele.rfi_request.includes(k.name));
      ele['document_input_type'] = 'TEXT';
    }
    elementFieldJson &&
      elementFieldJson.forEach((field) => {
        //setting form section names
        field['code'] = ele.document_code;
        field['field_name'] = ele.type + '.' + ele.document_code + '.' + field.name;
        if (disabledFields.includes(field['field_name'])) field.disabled = true;
        if (field.onChange) {
          field.onChange[0].field_name =
            ele.type + '.' + ele.document_code + '.' + field.onChange[0].name;
          let url_params = field.onChange[0].url_params;
          if (url_params) {
            Object.keys(url_params).forEach(
              (ki) => (url_params[ki] = ele.type + '.' + ele.document_code + '.' + url_params[ki])
            );
          }
          dependentFields.push(ele.type + '.' + ele.document_code + '.' + field.name);
        }
        if (field.url_params) {
          Object.keys(field.url_params).forEach((ki) => {
            field.url_params[ki] = ele.type + '.' + ele.document_code + '.' + field.url_params[ki];
          });
        }
        if (
          ele.rfi_id &&
          ele.document_code === 'RFI_REASON_FOR_TRANSFER' &&
          ele.destination_currency
        ) {
          field['url'] += `&destination_currency=${ele.destination_currency}`;
        }
      });
    if (ele.everification_meta) {
      let field_data = ele.everification_meta.everification_request_field_data;
      field_data &&
        Object.keys(field_data).forEach((k) => {
          field_data[k] = ele.type + '.' + ele.document_code + '.' + field_data[k];
        });
    }
    ele['file_field_name'] =
      ele.type +
      '.' +
      ele.document_code +
      '.' +
      `${ele.type}_${ele.document_code}`.toLowerCase() +
      '_doc_file';
    let existingDocs = documentDetails.filter(
      (key) => key.document_code === ele.document_code && key.type === ele.type
    );
    if (existingDocs) {
      //file initialization
      existingDocs.forEach((doc) => {
        if (doc.document_detail_id && doc.is_document_uploaded) {
          ele[doc.is_back_document ? 'fileSrcBack' : 'fileSrc'] =
            API_URL + '/static/document/' + doc.document_detail_id;
          ele[doc.is_back_document ? 'actual_file_name_back' : 'actual_file_name'] =
            doc.actual_file_name;
        }
      });
    }
    processesdConfig[ele.type][ele.sequence][length - 1].fields_json = elementFieldJson;
  });
  return { processesdConfig, dependentFields };
}

/**
 * function to construct initialization obj for document - fields initialization
 * @param {object} documentDetails
 */
export function constructInitializationObj(documentData, requiredDocTypes) {
  let initObj = {},
    documentDetails = documentData.filter(
      (doc) =>
        requiredDocTypes.includes(doc.type) && !DONOT_INITIALIZE_DOCS.includes(doc.document_code)
    );
  if (documentDetails)
    Object.keys(documentDetails).forEach((docData) => {
      docData = documentDetails[docData];
      if (!initObj[docData.type]) {
        initObj[docData.type] = {};
      }
      if (!initObj[docData.type][docData.document_code]) {
        initObj[docData.type][docData.document_code] = {};
      }
      let { identity_number, date_of_expiry, date_of_issue, document_code, document_detail_id } =
        docData;
      initObj[docData.type][document_code] = {
        ...initObj[docData.type][document_code],
        ...docData.details,
        identity_number,
        date_of_expiry,
        date_of_issue
      };
      if (docData.document_detail_id && docData.is_document_uploaded) {
        initObj[docData.type][document_code][
          `${docData.type}_${docData.document_code}`.toLowerCase() +
            (docData.is_back_document ? '_doc_file_back' : '_doc_file')
        ] = document_detail_id; // file initialization
      }
    });
  return initObj;
}

/**
 * function to construct payload for document upload
 * @param {object} props - all form values assumption - form consists of documents only
 */
export function processSubmitPayload(
  props,
  documentDetails = [],
  vision_doc_code,
  deletePayoad,
  trancKycObj = {},
  rfiWrapper
) {
  let allTypes = [];
  let documents = [],
    fileArr = [],
    fieldArr = [],
    files = {},
    flatProps = flattenObject(props);
  fileArr = Object.keys(flatProps).filter(
    (key) => flatProps[key] instanceof File || flatProps[key] instanceof Blob
  );
  fieldArr = Object.keys(flatProps).filter(
    (key) => !(flatProps[key] instanceof File || flatProps[key] instanceof Blob)
  );
  let linkLessDoc = Object.keys(flatProps).find((key) => {
    let code = key.split('.');
    code = code[1];
    return TEXT_TYPE_DOCUMENTS.includes(code);
  });
  if (linkLessDoc) {
    fileArr.push(linkLessDoc);
  }

  fileArr.forEach((doc, i) => {
    let kycObject = rfiWrapper
      ? {
          is_KYC_oriented:
            trancKycObj[i] && trancKycObj[i].is_KYC_oriented
              ? trancKycObj[i].is_KYC_oriented
              : false,
          is_transaction_oriented:
            trancKycObj[i] && trancKycObj[i].is_transaction_oriented
              ? trancKycObj[i].is_transaction_oriented
              : false
        }
      : {
          is_KYC_oriented: true,
          is_transaction_oriented: false
        };
    let ob = {
        document_detail_id: null,
        ...kycObject,
        identity_number: null,
        date_of_issue: null,
        date_of_expiry: null,
        details: {},
        ...(vision_doc_code || {})
      },
      docNameArr = doc.split('.');
    ob.type = docNameArr[0];
    if (!allTypes.includes(docNameArr[0])) allTypes.push(docNameArr[0]);
    ob.code = docNameArr[1];
    ob.fieldname = docNameArr[2];
    ob.is_back_document = ob.fieldname.indexOf('back') !== -1;
    let initDocObj = documentDetails.find(
      (key) =>
        key.type === ob.type &&
        key.document_code === ob.code &&
        ((key.is_back_document === undefined && ob.is_back_document === false) ||
          key.is_back_document === ob.is_back_document)
    );
    if (initDocObj) {
      ob.document_detail_id = initDocObj.document_detail_id;
      if (initDocObj.details) {
        ob.details = { ...ob.details, ...initDocObj.details };
      }
      ob.date_of_issue = initDocObj.date_of_issue;
      ob.date_of_expiry = initDocObj.date_of_expiry;
    }
    Object.keys(flatProps).forEach((key) => {
      let keyNameArr = key.split('.');
      if (keyNameArr[0] === ob.type && keyNameArr[1] === ob.code) {
        if (flatProps[key] instanceof File || flatProps[key] instanceof Blob) {
          files[keyNameArr[2]] = flatProps[key];
        } else if (!EXCLUDE_KEYS_IN_DOC_DETAILS.includes(keyNameArr[2])) {
          if (
            ENCRYPT_DATA_FOR_DOCUMENT_CODE.includes(keyNameArr[1]) &&
            keyNameArr[2] === 'identity_number'
          ) {
            ob.details[keyNameArr[2]] = encryptData(flatProps[key]);
          } else ob.details[keyNameArr[2]] = flatProps[key];
        } else {
          if (
            ENCRYPT_DATA_FOR_DOCUMENT_CODE.includes(keyNameArr[1]) &&
            keyNameArr[2] === 'identity_number'
          ) {
            ob[keyNameArr[2]] = encryptData(flatProps[key]);
          } else ob[keyNameArr[2]] = flatProps[key];
        }
      }
    });
    documents.push(ob);
  });
  Object.keys(props).forEach((k) => {
    if ((deletePayoad && deletePayoad.includes(k)) || k.includes('_btn_radio')) {
      delete props[k];
    }
  });

  return { documents, files, fieldProps: fieldArr, allTypes, formData: props };
}

export const processNestedConfigRecursive = (obj, loopArrZerothElement) => {
  // If obj is null or undefined, return early
  if (!obj) return;

  // If obj is an array, process each element
  if (Array.isArray(obj)) {
    obj.forEach((item) => processNestedConfigRecursive(item, loopArrZerothElement));
    return;
  }

  // If obj is an object
  if (typeof obj === 'object') {
    // If this object has a 'name' property, add code and field_name
    if ('name' in obj) {
      obj.code = loopArrZerothElement?.document_code;
      obj.field_name =
        `RFI_ID:` +
        loopArrZerothElement?.rfi_id +
        `.` +
        loopArrZerothElement?.document_code +
        `.` +
        obj.name;
    }

    // Recursively process all properties of this object
    Object.keys(obj).forEach((key) => {
      processNestedConfigRecursive(obj[key], loopArrZerothElement);
    });
  }
};

/**
 * jsx field generator function
 * @param {array} fields_config
 */
export const fieldsRenderer = (
  fields_config,
  disableForm,
  allFormValues,
  options,
  onRadioButtonChangedNestedFields,
  isNewUi = false
) => {
  let fieldArr = [],
    meta_field = [];
  let isCorp = options && options.isCorp ? options.isCorp : false;
  const loopArrZerothElement = options?.loopArr?.[0] || {};
  if (fields_config) {
    // radio buttons not required
    fieldArr = fields_config.map((field) => {
      switch (field.category) {
        case 'text_field':
          return (
            <Grid.Column
              computer={7}
              mobile={16}
              tablet={16}
              className="fields-col"
              key={field.field_name}
            >
              <Field
                key={field.field_name}
                disabled={disableForm || field.disabled}
                name={field.field_name}
                type={field.type}
                className="input form-flow"
                component={RenderTextField}
                placeholder={field.label}
                validate={
                  disableForm || field.disabled
                    ? []
                    : [
                        (value, allValues, props, name) => {
                          let errors;
                          errors = dynamic_validator2(value, field.validations, errors, {
                            isCorp,
                            allValues,
                            name
                          });
                          //push the dummy word validation only if the RFI is for bene address
                          if (
                            !errors &&
                            field.validations?.required &&
                            loopArrZerothElement.entity_type === 'BENEFICIARY' &&
                            loopArrZerothElement.document_code === 'ADDRESS'
                          ) {
                            errors = isValidBeneAddressInput(value);
                          }
                          return errors;
                        }
                      ]
                }
                normalize={
                  field.validations && field.validations.length && field.validations.length.max
                    ? r_maxLength(field.validations.length.max)
                    : null
                }
              />
              {field.show_sample && (
                <div className="field-email-description">
                  {field.note}
                  {field.show_sample && options?.onViewSampleClick && (
                    <span
                      className="view-sample-link"
                      onClick={() => options.onViewSampleClick(field)}
                    >
                      {' '}
                      View sample
                    </span>
                  )}
                </div>
              )}
            </Grid.Column>
          );
        case 'dropdown': {
          const fieldKey = field.field_name?.split('.')?.pop();
          let additional_props = field.additional_props || {};
          let bindToThisObject = allFormValues
            ? flattenObject(allFormValues)[field.field_name]
            : null;

          if (
            options?.shouldPrefillAddress === true &&
            (!bindToThisObject || bindToThisObject === '') &&
            fieldKey &&
            options?.userAddress?.[fieldKey]
          ) {
            bindToThisObject = options.userAddress[fieldKey];
          }
          let dependedOnObject = field.dependedOnObject
            ? flattenObject(allFormValues)[field.dependedOnObject]
            : null;
          let query = '';
          if (field.field_name?.endsWith('KYC_TYPE.kyc_type')) {
            /* Manually adding Country code query param to KYC Type dropdown as its a common render function for RFIs.
             query will get overwritten if we have url_params in field object and options 
             */
            const reduxStore = store.getState();
            const countryCode = get(reduxStore, 'rootReducer.userStatusReducer.country_code', '');
            query = `&country_code=${countryCode}`;
          }
          if (field.url && field.url_params && options) {
            let { dispatch, change, formName } = options;
            if (allFormValues && allFormValues[field.field_name]) {
              //make the dropdown value empty, only if the dropdown has some value
              dispatch(change(formName, field.field_name, ''));
            }
            let params = field.url_params;
            Object.keys(params).forEach((ki) => {
              if (flattenObject(allFormValues)[params[ki]])
                query = `&${ki}=${flattenObject(allFormValues)[params[ki]]}`;
            });
          }
          if (field.onChange && flattenObject(allFormValues)[field.field_name]) {
            field.onChange[0].dependedOnObject = field.field_name;
            meta_field = fieldsRenderer(field.onChange, false, allFormValues, options);
          }
          let dropdown =
            (field.dependedOnObject && dependedOnObject) || !field.dependedOnObject ? (
              <Grid.Column
                width={7}
                computer={7}
                mobile={16}
                tablet={16}
                className="fields-col dropdown"
                key={field.field_name}
              >
                <Field
                  {...additional_props}
                  key={field.field_name}
                  name={field.field_name}
                  enableThis
                  disabled={disableForm || field.disabled}
                  type={field.type}
                  bindToThisObject={bindToThisObject}
                  dependedOnObject={dependedOnObject}
                  className="input form-flow"
                  component={isNewUi ? CommonDropdownOutlined : CommonDropdown}
                  placeholder={field.label}
                  validate={[
                    (value, allValues, props, name) => {
                      let errors;
                      errors = dynamic_validator2(value, field.validations, errors, {
                        isCorp,
                        allValues,
                        name
                      });
                      return errors;
                    }
                  ]}
                  url={field.static_values ? null : API_URL + field.url + query}
                  staticValues={field.static_values}
                />
              </Grid.Column>
            ) : null;
          return dropdown;
        }
        case 'date_picker':
          return (
            <Grid.Column
              width={7}
              computer={7}
              mobile={16}
              tablet={16}
              className="fields-col  date-picker"
              key={field.field_name}
            >
              <Field
                key={field.field_name}
                name={field.field_name}
                type={field.type}
                disabled={disableForm || field.disabled}
                className="input form-flow"
                component={RenderDatePicker}
                parse={(val) => val && moment(val).format('DD/MM/YYYY')}
                placeholder={field.label}
                validate={[
                  (value, allValues, props, name) => {
                    let errors;
                    errors = dynamic_validator2(value, field.validations, errors, {
                      isCorp,
                      allValues,
                      name
                    });
                    return errors;
                  }
                ]}
                minDate={
                  field.range &&
                  field.range.min_identifier &&
                  moment().add(field.range.min_offset, field.range.min_identifier)
                }
                maxDate={
                  field.range &&
                  field.range.max_identifier &&
                  moment().add(field.range.max_offset, field.range.max_identifier)
                }
              />
            </Grid.Column>
          );
        case 'radio':
          return (
            <Grid.Column
              width={7}
              computer={7}
              mobile={16}
              tablet={16}
              className="fields-col"
              key={field.field_name}
            >
              <div className="radio-fatca-wrapper">
                <label className="radio-label">
                  <Field name={field.field_name} component={renderRadioButtonError} />
                </label>
                <label id={`radio-${field.label}`} key={field.field_name} className="label-text">
                  {field.label}
                  <div className="radio-fields">
                    {field.values &&
                      field.values.map((v, index) => {
                        let fieldId = `${field.field_name}-${v.value}`;
                        processNestedConfigRecursive(v, loopArrZerothElement);

                        return [
                          <Field
                            key={field.field_name}
                            validate={field.validations?.required ? [requiredradio] : null}
                            name={field.field_name}
                            component="input"
                            type={field.type}
                            className={field.type}
                            parse={(val) => (val === 'true' ? true : val === 'false' ? false : val)}
                            disabled={disableForm || field.disabled} //Needs to be fixed
                            value={v.value}
                            id={fieldId}
                            onChange={() => onRadioButtonChangedNestedFields?.(v)}
                          />,
                          <label
                            id={v.label}
                            key={index}
                            className="inline-block-span"
                            htmlFor={fieldId}
                          >
                            {v.label}
                          </label>
                        ];
                      })}
                  </div>
                </label>
              </div>
            </Grid.Column>
          );
        default:
          return null;
      }
    });
  }
  return [...fieldArr, ...meta_field];
};

export function shouldDisableFields(documentDetails, doctype, dispatch, disableDocs, is_corporate) {
  if (disableDocs) return { disableForm: true, disabledFields: true, disableLinks: true };
  let disableForm = false,
    disableFields = false,
    disableLinks = false;
  if (documentDetails && documentDetails.length > 0) {
    let typeDocs = documentDetails.filter((key) => key.type === doctype) || [];
    typeDocs.forEach((docELe) => {
      // eslint-disable-next-line no-mixed-operators
      if (
        (is_corporate && docELe && (docELe.type.includes('POI') || docELe.type.includes('POA'))) ||
        (!is_corporate && docELe && docELe.document_detail_id && !disableForm && !disableFields)
      ) {
        if (docELe.is_document_uploaded) {
          disableForm = true;
          dispatch(updateDocsUploadedOrEverifiedSuccess(true));
        } else if (docELe.is_everified && docELe.status === 'APPROVED') {
          dispatch(updateDocsUploadedOrEverifiedSuccess(true));
          if (MANDATORY_DOCUMENT_UPLOAD_FOR.includes(docELe.document_code)) {
            disableFields = true;
          } else {
            disableForm = true;
          }
        } else if (
          docELe.is_everified &&
          docELe.status === 'DRAFT' &&
          DISABLE_FIELDS_ON_EVERIFICATION_ATTEMPT.includes(docELe.document_code)
        ) {
          dispatch(updateDocsUploadedOrEverifiedSuccess(true));
          if (MANDATORY_DOCUMENT_UPLOAD_FOR.includes(docELe.document_code)) {
            disableFields = true;
          } else {
            disableForm = true;
          }
        }
      }
    });
  }
  return {
    disableForm,
    disableFields,
    disableLinks
  };
}
